import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2Icon } from "lucide-react";
import { Button } from "@penmate/react-shared/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { trackMetric } from "@/api/metrics";
import type { PmUser } from "@/api/user";

const INTRO_VERSION = 1;
export const INTRO_LOCALSTORAGE_KEY = `pmreact.messaging.intro.v${INTRO_VERSION}.dismissed`;

const METRICS_KEY_DISMISSED = `pmreact_messaging_intro_v${INTRO_VERSION}_dismissed`;
const METRICS_KEY_DISMISSED_AT = `pmreact_messaging_intro_v${INTRO_VERSION}_dismissed_at`;

const getUserMetrics = (user: PmUser | null): Record<string, unknown> => {
  const raw = user?.raw;
  if (!raw || typeof raw !== "object") return {};
  const metrics = (raw as any).metrics;
  if (!metrics || typeof metrics !== "object" || Array.isArray(metrics)) return {};
  return metrics as Record<string, unknown>;
};

const readDismissed = (): boolean => {
  try {
    const v = window.localStorage.getItem(INTRO_LOCALSTORAGE_KEY);
    return v === "1" || v === "true";
  } catch {
    return false;
  }
};

const writeDismissed = () => {
  try {
    window.localStorage.setItem(INTRO_LOCALSTORAGE_KEY, "1");
  } catch {
    // ignore
  }
};

const readDismissedFromUser = (user: PmUser | null): boolean => {
  const metrics = getUserMetrics(user);
  const v = (metrics as any)[METRICS_KEY_DISMISSED];
  return v === true || v === "true" || v === 1 || v === "1";
};

export function useNewMessengerIntro(args: { enabled: boolean; user: PmUser | null }) {
  const userLoaded = args.user !== null;
  const [dismissed, setDismissed] = useState<boolean>(() => (typeof window === "undefined" ? true : readDismissed()));
  const [open, setOpen] = useState(false);
  const didTrackShownRef = useRef(false);

  const dismissedOnServer = useMemo(() => readDismissedFromUser(args.user), [args.user]);

  // If local storage is cleared but the server says it's already dismissed, treat it as dismissed
  // and restore the local flag to prevent re-showing on this device.
  useEffect(() => {
    if (dismissed) return;
    if (!dismissedOnServer) return;
    writeDismissed();
    setDismissed(true);
  }, [dismissed, dismissedOnServer]);

  const shouldShow = useMemo(() => {
    if (!args.enabled) return false;
    if (!userLoaded) return false;
    if (dismissed) return false;
    if (dismissedOnServer) return false;
    return true;
  }, [args.enabled, dismissed, dismissedOnServer, userLoaded]);

  useEffect(() => {
    if (!dismissedOnServer) return;
    setOpen(false);
  }, [dismissedOnServer]);

  useEffect(() => {
    if (!shouldShow) return;
    setOpen(true);
  }, [shouldShow]);

  useEffect(() => {
    if (!open) return;
    if (didTrackShownRef.current) return;
    didTrackShownRef.current = true;
    // Best-effort server persistence: this ends up in users.metrics via event="add-metrics".
    void trackMetric("add-metrics", {
      pmreact_messaging_intro_version: INTRO_VERSION,
      pmreact_messaging_intro_shown: true,
      pmreact_messaging_intro_shown_at: new Date().toISOString(),
    });
  }, [open]);

  const dismiss = useCallback(() => {
    if (dismissed) {
      setOpen(false);
      return;
    }
    writeDismissed();
    setDismissed(true);
    setOpen(false);
    void trackMetric("add-metrics", {
      [METRICS_KEY_DISMISSED]: true,
      [METRICS_KEY_DISMISSED_AT]: new Date().toISOString(),
    });
  }, [dismissed]);

  return {
    open,
    dismiss,
    setOpen,
  };
}

export function NewMessengerIntroDialog(props: {
  open: boolean;
  onDismiss: () => void;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <Dialog
      open={props.open}
      onOpenChange={(next) => {
        props.onOpenChange?.(next);
        if (!next) props.onDismiss();
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-sm !p-5" data-testid="new-messenger-intro">
        <DialogTitle className="text-base">Welcome to the new Messenger</DialogTitle>
        <DialogDescription className="mt-1">
          A cleaner experience for writing, sending, and tracking messages.
        </DialogDescription>

        <div className="mt-3 space-y-2 text-sm text-foreground">
          <div className="flex items-start gap-2">
            <CheckCircle2Icon className="mt-0.5 h-4 w-4 text-[#4b46a4a3]" aria-hidden="true" />
            <div>A unified place to send, receive, and view messages</div>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle2Icon className="mt-0.5 h-4 w-4 text-[#4b46a4a3]" aria-hidden="true" />
            <div>Smoother photo uploads and previews</div>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle2Icon className="mt-0.5 h-4 w-4 text-[#4b46a4a3]" aria-hidden="true" />
            <div>Delivery details and tracking in one place</div>
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <Button type="button" onClick={props.onDismiss}>
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
